/**
 * Seeded mulberry32 streams. State is a plain 32-bit integer so `World.rng` snapshots and
 * compares as data. Integer ops only (`Math.imul`, shifts), identical on every JS engine.
 */
export interface Rng {
  state: number
}

export interface RngStreams {
  crates: Rng
  bots: Rng
  hazards: Rng
}

// Fixed, distinct salts so each consumer gets an independent stream from one match seed.
const CRATES_SALT = 0x9e3779b9
const BOTS_SALT = 0x85ebca6b
const HAZARDS_SALT = 0xc2b2ae35
const TWO_POW_32 = 4294967296

export function createRng(seed: number): Rng {
  return { state: seed | 0 }
}

/** Next unsigned 32-bit integer. */
export function nextU32(rng: Rng): number {
  rng.state = (rng.state + 0x6d2b79f5) | 0
  let t = rng.state
  t = Math.imul(t ^ (t >>> 15), t | 1)
  t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t
  return (t ^ (t >>> 14)) >>> 0
}

/** Uniform integer in `[0, n)`. `n` must be a positive integer. */
export function nextInt(rng: Rng, n: number): number {
  if (!Number.isInteger(n) || n <= 0) throw new RangeError(`nextInt: n must be > 0, got ${n}`)
  return Math.floor((nextU32(rng) / TWO_POW_32) * n)
}

/** Uniform integer in `[lo, hi]`, both inclusive. */
export function nextRange(rng: Rng, lo: number, hi: number): number {
  return lo + nextInt(rng, hi - lo + 1)
}

function deriveStream(seed: number, salt: number): Rng {
  // One mulberry32 step mixes seed and salt so nearby seeds do not give correlated streams.
  return createRng(nextU32(createRng(seed ^ salt)))
}

export function createRngStreams(seed: number): RngStreams {
  return {
    crates: deriveStream(seed, CRATES_SALT),
    bots: deriveStream(seed, BOTS_SALT),
    hazards: deriveStream(seed, HAZARDS_SALT),
  }
}
